import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

export default function NotificationBell() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items || []);
      setUnread(data.unread || 0);
    } catch (err) { /* not logged in */ }
  };

  useEffect(() => {
    if (!user) return;
    load();
    const t = setInterval(load, 60_000); // refresh every 60s
    return () => clearInterval(t);
  }, [user]);

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  if (!user) return null;

  const markAll = async () => {
    try { await api.post("/notifications/read", {}); load(); } catch {}
  };

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setOpen((o) => !o)}
        data-testid="notif-bell"
        aria-label="Notifications"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-unread-count" className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-bold text-accent-foreground">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </Button>
      {open && (
        <div className="fixed inset-x-3 top-[calc(3.5rem+env(safe-area-inset-top,0px))] z-[70] rounded-xl border border-border bg-card p-2 shadow-xl sm:absolute sm:inset-x-auto sm:right-0 sm:top-full sm:mt-2 sm:w-80" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-2 py-1">
            <div className="text-sm font-semibold">Notifications</div>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-read" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                <Check className="h-3 w-3" /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-[min(24rem,60vh)] divide-y divide-border overflow-y-auto">
            {items.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">All caught up</div>
            ) : items.slice(0, 20).map((n) => (
              <Link
                key={n.notification_id}
                to={n.link || "/dashboard"}
                onClick={() => setOpen(false)}
                data-testid={`notif-item-${n.kind}`}
                className={`block px-3 py-3 transition-colors hover:bg-secondary ${!n.read ? "bg-primary/5" : ""}`}
              >
                <div className="text-sm font-medium">{n.title}</div>
                <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{n.body}</div>
                <div className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground">{new Date(n.created_at).toLocaleString()}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
